import React, { useState, useEffect, useCallback } from 'react';
import './TabInseminacion.css';
import API_CONFIG from '../config';
import { apiFetch } from '../auth';

const BASE = API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.INSEMINACIONES;

const EMPTY = {
  fecha: new Date().toISOString().split('T')[0],
  observaciones: '',
};

export default function TabInseminacion({ animalId, onInseminacionActualizada }) {
  const [registros, setRegistros]     = useState([]);
  const [loading, setLoading]         = useState(false);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando]       = useState(null);
  const [form, setForm]               = useState(EMPTY);
  const [guardando, setGuardando]     = useState(false);
  const [error, setError]             = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${BASE}/animal/${animalId}`);
      if (!res.ok) throw new Error('Error al cargar registros de inseminación');
      setRegistros(await res.json());
    } catch (e) {
      console.error(e);
      setRegistros([]);
    } finally {
      setLoading(false);
    }
  }, [animalId]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNuevo = () => {
    setEditando(null);
    setForm(EMPTY);
    setError('');
    setMostrarForm(true);
  };

  const abrirEditar = (r) => {
    setEditando(r.id);
    setForm({
      fecha:         r.fecha         || '',
      observaciones: r.observaciones || '',
    });
    setError('');
    setMostrarForm(true);
  };

  const guardar = async (e) => {
    e.preventDefault();
    if (!form.fecha) return setError('La fecha es obligatoria.');

    setGuardando(true); setError('');
    try {
      const payload = { ...form, animalId: parseInt(animalId) };
      const url     = editando ? `${BASE}/${editando}` : BASE;
      const method  = editando ? 'PUT' : 'POST';

      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Error al guardar');
      }
      await cargar();
      setMostrarForm(false);
      setEditando(null);
      if (onInseminacionActualizada) onInseminacionActualizada();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este registro de inseminación?')) return;
    try {
      await apiFetch(`${BASE}/${id}`, { method: 'DELETE' });
      await cargar();
      if (onInseminacionActualizada) onInseminacionActualizada();
    } catch (e) {
      alert('Error al eliminar');
    }
  };

  const formatFecha = (f) => f ? new Date(f + 'T00:00:00').toLocaleDateString('es-PY') : '-';

  return (
    <div className="tab-inseminacion">
      <div className="tab-toolbar">
        <div>
          <h3>🐄 Historial de Inseminaciones</h3>
          {registros.length > 0 && (
            <span className="inseminacion-total-badge">Total: <strong>{registros.length}</strong></span>
          )}
        </div>
        <button className="btn-primary btn-sm" onClick={abrirNuevo}>+ Registrar Inseminación</button>
      </div>

      {loading ? (
        <div className="loading">Cargando...</div>
      ) : registros.length === 0 ? (
        <div className="empty-state">
          <p>🐄 No hay registros de inseminación para este animal.</p>
          <button className="btn-primary btn-sm" onClick={abrirNuevo}>Registrar primera inseminación</button>
        </div>
      ) : (
        <table className="inseminacion-tabla">
          <thead>
            <tr>
              <th>#</th>
              <th>Fecha</th>
              <th>Observaciones</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {registros.map((r, idx) => (
              <tr key={r.id} className={idx === 0 ? 'fila-ultimo' : ''}>
                <td className="idx-col">
                  {idx === 0 && <span className="badge-ultimo">Último</span>}
                  {idx > 0 && <span className="num-col">{registros.length - idx}</span>}
                </td>
                <td><strong>{formatFecha(r.fecha)}</strong></td>
                <td className="obs-col">{r.observaciones || '-'}</td>
                <td>
                  <div className="acciones">
                    <button className="btn-info btn-sm" onClick={() => abrirEditar(r)}>✏️</button>
                    <button className="btn-danger btn-sm" onClick={() => eliminar(r.id)}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Formulario modal */}
      {mostrarForm && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setMostrarForm(false)}>
          <div className="modal modal-sm">
            <div className="modal-header">
              <h2>{editando ? 'Editar Registro' : 'Registrar Inseminación'}</h2>
              <button className="modal-close" onClick={() => setMostrarForm(false)}>✕</button>
            </div>
            <form onSubmit={guardar}>
              {error && <div className="alert alert-danger">{error}</div>}
              <div className="form-grid">
                <div className="form-group full">
                  <label>Fecha *</label>
                  <input type="date" value={form.fecha}
                         onChange={e => setForm(p => ({...p, fecha: e.target.value}))} required />
                </div>
                <div className="form-group full">
                  <label>Observaciones</label>
                  <textarea value={form.observaciones}
                            onChange={e => setForm(p => ({...p, observaciones: e.target.value.toUpperCase()}))}
                            rows={3} placeholder="Notas de la inseminación..."
                            style={{textTransform:'uppercase'}} />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setMostrarForm(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={guardando}>
                  {guardando ? 'Guardando...' : '✓ Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
